const TZ_ALIASES: Record<string, string> = {
  'Europe/Kiev': 'Europe/Kyiv',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Europe/Nicosia': 'Asia/Nicosia',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
};

// Mirrors the vessel table default: the backend stores 'UTC' when no zone is supplied.
export const DEFAULT_TIME_ZONE = 'UTC';

// Intl.supportedValuesOf is unavailable on older WebViews (pre-iOS 15.4 / Chrome 99), which
// the Capacitor builds can still land on. Keep a short list so the picker is never empty.
const FALLBACK_TIME_ZONES = [
  'UTC',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Miquelon',
  'America/New_York',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Europe/Athens',
  'Europe/Kyiv',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Monaco',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Rome',
  'Pacific/Auckland',
];

export function getBrowserTimeZone(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TZ_ALIASES[tz] ?? tz;
}

/** IANA zone identifiers accepted by the backend, sorted for display. */
export function getSupportedTimeZones(): string[] {
  const zones =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : FALLBACK_TIME_ZONES;

  // supportedValuesOf omits UTC on some engines, and the browser zone is missing from the fallback.
  return [...new Set([...zones, DEFAULT_TIME_ZONE, getBrowserTimeZone()])].sort((a, b) =>
    a.localeCompare(b),
  );
}

/**
 * Matches the backend's time.LoadLocation check so an unknown zone is caught in the form
 * instead of coming back as a 400.
 */
export function isValidTimeZone(tz: string): boolean {
  if (!tz) return false;

  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Current UTC offset of a zone, e.g. `GMT-4`. Empty when the engine cannot resolve it. */
export function getTimeZoneOffsetLabel(tz: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);

    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Picker label, e.g. `America/New_York (GMT-4)`. */
export function formatTimeZoneLabel(tz: string): string {
  const offset = getTimeZoneOffsetLabel(tz);
  const name = tz.replace(/_/g, ' ');

  return offset ? `${name} (${offset})` : name;
}

/** The calendar day an instant falls on in a zone, `YYYY-MM-DD`. */
export function dayInTimeZone(at: Date, tz: string): string {
  try {
    // Built from the parts: a locale's own order and separators vary between ICU versions.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
  } catch {
    return at.toISOString().slice(0, 10);
  }
}

/**
 * Today's calendar date in a zone, `YYYY-MM-DD`. A temporary contract's days are the vessel's,
 * so "today" is the vessel's today, not the browser's.
 */
export function todayInTimeZone(tz: string, at: Date = new Date()): string {
  return dayInTimeZone(at, tz);
}

/** How far a zone's wall clock is ahead of UTC at an instant, in milliseconds. */
function zoneOffsetMs(at: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const wall = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  );
  return wall - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * The instant a `YYYY-MM-DD` day begins in a zone. Worked out twice, so a day whose offset
 * differs from the one before it (a DST switch) still lands on its own midnight. An unknown
 * zone is read as UTC.
 */
export function zonedMidnight(day: string, tz: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  const wall = Date.UTC(year, month - 1, date);
  try {
    const first = wall - zoneOffsetMs(new Date(wall), tz);
    return new Date(wall - zoneOffsetMs(new Date(first), tz));
  } catch {
    return new Date(wall);
  }
}

/**
 * A `YYYY-MM-DD` calendar day as local midnight, which is how `DateTimePicker` reads its value
 * and bounds: the day the user sees is the day that was stored, whatever the browser zone.
 */
export function calendarDayToLocalDate(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

/** The calendar day a `DateTimePicker` value names, read in the browser zone it was picked in. */
export function localDateToCalendarDay(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `days` calendar days after `day` (before, when negative), both `YYYY-MM-DD`. */
export function addCalendarDays(day: string, days: number): string {
  const date = calendarDayToLocalDate(day);
  date.setDate(date.getDate() + days);
  return localDateToCalendarDay(date);
}
